import React, { useEffect, useRef, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';

interface BoxItem {
  id: string;
  label: string;
  isEditing?: boolean;
}

interface AddRemoveComponentProps {
  initialLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  disabled?: boolean;
  editable?: boolean;
  title?: string;
  droppableId?: string;
}

export const AddRemoveComponent: React.FC<AddRemoveComponentProps> = ({
  initialLabels,
  onLabelsChange,
  disabled,
  editable,
  droppableId = 'default-droppable',
}) => {
  const { t } = useTranslation(NS.adminPages);
  const UI = ADMIN_PAGES_KEYS.jobOpenings.ui;
  const [boxes, setBoxes] = useState<BoxItem[]>(() => {
    return initialLabels.map((label, index) => ({
      id: `${label}-${index}-${Date.now()}`,
      label,
      isEditing: false,
    }));
  });
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const firstRenderRef = useRef(true);
  const skipUpdateRef = useRef(false);

  useEffect(() => {
    if (skipUpdateRef.current) {
      skipUpdateRef.current = false;
      return;
    }

    const labels = boxes.map((box) => box.label);
    onLabelsChange(labels);
  }, [boxes]);

  useEffect(() => {
    if (!firstRenderRef.current) {
      const currentLabels = boxes.map((box) => box.label);
      const isSame =
        initialLabels.length === currentLabels.length &&
        initialLabels.every((label, index) => label === currentLabels[index]);

      if (isSame) return;

      skipUpdateRef.current = true;
      setBoxes(
        initialLabels.map((label, index) => ({
          id: `${label}-${index}`,
          label,
          isEditing: false,
        })),
      );
    }

    firstRenderRef.current = false;
  }, [initialLabels]);

  const handleAddBox = () => {
    if (inputValue.trim()) {
      const newBox: BoxItem = {
        id: `${inputValue}-${boxes.length}-${Date.now()}`,
        label: inputValue,
        isEditing: false,
      };
      setBoxes([...boxes, newBox]);
      setInputValue('');
    }
    setShowInput(false);
  };

  const handleRemove = (id: string) => {
    setBoxes(boxes.filter((box) => box.id !== id));
  };

  const handleStartEdit = (id: string) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return { ...box, isEditing: true };
        }
        return { ...box, isEditing: false };
      }),
    );
  };

  const handleStopEdit = (id: string, newLabel: string) => {
    setBoxes((prevBoxes) =>
      prevBoxes.map((box) => {
        if (box.id === id) {
          return { ...box, label: newLabel, isEditing: false };
        }
        return box;
      }),
    );
  };

  const handleClickAdd = () => {
    setShowInput(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 150 }}>
      {/* Content Area */}
      <Droppable droppableId={droppableId} direction="horizontal">
        {(provided) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.25,
              mb: 2,
              minHeight: 80,
            }}
          >
            {boxes.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 3,
                  width: '100%',
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 3,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    color: 'grey.400',
                  }}
                >
                  <Plus size={20} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {editable ? t(UI.noItemsAvailable) : t(UI.noItemsAdded)}
                </Typography>
              </Box>
            ) : (
              boxes.map((box, index) => (
                <Draggable
                  key={box.id}
                  draggableId={box.id}
                  index={index}
                  isDragDisabled={disabled || box.isEditing}
                >
                  {(provided, snapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      elevation={snapshot.isDragging ? 4 : 0}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.75,
                        px: 1.5,
                        minWidth: 140,
                        maxWidth: { xs: '100%', md: 300 },
                        borderRadius: 2,
                        border: 1,
                        borderColor: box.isEditing
                          ? 'success.main'
                          : snapshot.isDragging
                            ? 'success.light'
                            : 'divider',
                        bgcolor: box.isEditing || snapshot.isDragging
                          ? 'success.50'
                          : 'background.paper',
                        cursor: 'grab',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'success.light',
                          '& .item-actions': {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      {box.isEditing ? (
                        <InputBase
                          inputRef={inputRef}
                          defaultValue={box.label}
                          autoFocus
                          onBlur={(e) => handleStopEdit(box.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleStopEdit(box.id, (e.target as HTMLInputElement).value);
                            }
                          }}
                          sx={{
                            width: '100%',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <>
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            noWrap
                            sx={{
                              flex: 1,
                              color: 'text.primary',
                              '&:hover': { color: !disabled ? 'success.main' : 'inherit' },
                            }}
                            title={box.label}
                          >
                            {box.label}
                          </Typography>
                          {!disabled && (
                            <Stack
                              className="item-actions"
                              direction="row"
                              spacing={0.25}
                              alignItems="center"
                              sx={{
                                opacity: 0,
                                transition: 'opacity 0.2s',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEdit(box.id);
                                }}
                                sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'success.main' } }}
                              >
                                <Pencil size={12} />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemove(box.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <Trash2 size={12} />
                              </IconButton>
                            </Stack>
                          )}
                        </>
                      )}
                    </Paper>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>

      {/* Input/Button Area */}
      <Box sx={{ mt: 'auto', pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
        {showInput ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              inputRef={inputRef}
              size="small"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBox()}
              onBlur={handleAddBox}
              placeholder={t(UI.typeSomething)}
              sx={{ flex: 1, '& input': { fontSize: '0.75rem', py: 0.75 } }}
            />
            <Button
              size="small"
              onClick={() => setShowInput(false)}
              sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
            >
              {t(UI.cancel)}
            </Button>
          </Stack>
        ) : (
          !disabled && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<Plus size={14} />}
              onClick={handleClickAdd}
              sx={{
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
              }}
            >
              {t(UI.addNew)}
            </Button>
          )
        )}
      </Box>
    </Box>
  );
};

export default AddRemoveComponent;
